import React, { useEffect, useMemo, useState } from 'react';
import { Copy, ExternalLink, HandCoins, Heart, Mail, QrCode } from 'lucide-react';
import QRCode from 'qrcode';
import ModalShell from '../ModalShell';
import { showToast } from '../../utils/toast';
import { buildPixCopyPasteCode } from '../../utils/pix';
import { CONTACT_EMAIL, CONTACT_MAILTO } from '../../constants/contact';

function SupportAmountButton({ item, genericLink }) {
  const href = item.link || genericLink;

  if (!href) {
    return (
      <button
        type="button"
        disabled
        className="h-12 rounded-2xl border border-dashed border-rose-200 dark:border-rose-900/40 bg-rose-50/70 dark:bg-rose-900/10 text-rose-400 dark:text-rose-300/60 text-sm font-black tracking-wide disabled:cursor-not-allowed"
      >
        {item.label}
      </button>
    );
  }

  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="h-12 rounded-2xl bg-rose-600 hover:bg-rose-700 text-white text-sm font-black tracking-wide flex items-center justify-center gap-2 transition-colors shadow-lg shadow-rose-600/20"
    >
      {item.label}
      <ExternalLink size={15} />
    </a>
  );
}

export default function SupportModal({ config, onClose }) {
  const [selectedPixAmount, setSelectedPixAmount] = useState(config.amounts[0]?.value || 10);
  const [pixQrCodeDataUrl, setPixQrCodeDataUrl] = useState('');
  const hasPix = Boolean(config.pixKey);
  const hasAnyLink = Boolean(config.genericPaymentLink || config.amounts.some((item) => item.link));
  const pixCopyPasteCode = useMemo(() => {
    if (!hasPix) return '';

    return buildPixCopyPasteCode({
      amount: selectedPixAmount,
      city: config.pixMerchantCity,
      key: config.pixKey,
      merchantName: config.pixMerchantName,
      txid: `APOIO${selectedPixAmount}`
    });
  }, [config.pixKey, config.pixMerchantCity, config.pixMerchantName, hasPix, selectedPixAmount]);

  useEffect(() => {
    if (!pixCopyPasteCode) {
      setPixQrCodeDataUrl('');
      return;
    }

    let isMounted = true;

    QRCode.toDataURL(pixCopyPasteCode, {
      errorCorrectionLevel: 'M',
      margin: 1,
      width: 320
    }).then((dataUrl) => {
      if (isMounted) {
        setPixQrCodeDataUrl(dataUrl);
      }
    }).catch(() => {
      if (isMounted) {
        setPixQrCodeDataUrl('');
      }
    });

    return () => {
      isMounted = false;
    };
  }, [pixCopyPasteCode]);

  const copyPixKey = async () => {
    if (!config.pixKey) return;

    try {
      await navigator.clipboard.writeText(config.pixKey);
      showToast('Chave Pix copiada!', { type: 'success' });
    } catch {
      showToast('Não foi possível copiar a chave Pix.', { type: 'error' });
    }
  };

  const copyPixCode = async () => {
    if (!pixCopyPasteCode) return;

    try {
      await navigator.clipboard.writeText(pixCopyPasteCode);
      showToast('Código Pix copia e cola copiado!', { type: 'success' });
    } catch {
      showToast('Não foi possível copiar o código Pix.', { type: 'error' });
    }
  };

  return (
    <ModalShell
      icon={Heart}
      maxWidthClass="max-w-2xl"
      onClose={onClose}
      subtitle="Apoio único para ajudar na manutenção e evolução do projeto."
      themeClass="bg-rose-600"
      title="Apoiar o projeto"
    >
      <div className="p-4 sm:p-6 md:p-7 max-h-[80vh] overflow-y-auto space-y-6 bg-gradient-to-br from-rose-50 via-pink-50 to-orange-50 dark:from-rose-950/35 dark:via-pink-950/25 dark:to-orange-950/25">
        <div className="rounded-3xl border border-rose-200/80 dark:border-rose-900/30 bg-gradient-to-br from-white via-rose-50 to-orange-50 dark:from-rose-950/30 dark:via-rose-950/20 dark:to-orange-950/20 p-4 sm:p-5 shadow-sm">
          <div className="flex items-start gap-4">
            <div className="h-12 w-12 shrink-0 rounded-2xl bg-gradient-to-br from-rose-500 to-orange-400 border border-white/50 flex items-center justify-center text-white shadow-lg shadow-rose-500/20">
              <HandCoins size={22} />
            </div>
            <div className="space-y-2">
              <p className="text-sm md:text-[15px] font-bold text-gray-800 dark:text-gray-100 leading-relaxed">
                {config.message}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Escolha um valor sugerido ou use o Pix, se preferir.
              </p>
            </div>
          </div>
        </div>

        <section className="space-y-3">
          <div className="flex items-center justify-between gap-3">
            <div>
              <h4 className="text-sm font-black uppercase tracking-wider text-gray-800 dark:text-gray-100">Contribuição única</h4>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Links de pagamento via {config.providerName}.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {config.amounts.map((item) => (
              <SupportAmountButton
                key={item.value}
                item={item}
                genericLink={config.genericPaymentLink}
              />
            ))}
          </div>

          {!hasAnyLink ? (
            <div className="rounded-2xl border border-dashed border-rose-200 dark:border-rose-900/40 bg-rose-50/80 dark:bg-rose-900/10 p-4 text-xs text-rose-800 dark:text-rose-200">
              Configure os links de pagamento nas variáveis `REACT_APP_SUPPORT_LINK_10`, `REACT_APP_SUPPORT_LINK_20`, `REACT_APP_SUPPORT_LINK_50` ou use um link genérico em `REACT_APP_SUPPORT_PAYMENT_LINK`.
            </div>
          ) : null}
        </section>

        <section className="space-y-3">
          <div>
            <h4 className="text-sm font-black uppercase tracking-wider text-gray-800 dark:text-gray-100">Pix</h4>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Para quem prefere contribuir diretamente via InfinitePay.
            </p>
          </div>

          <div className="rounded-3xl border border-rose-200/80 dark:border-rose-900/30 bg-white/80 dark:bg-rose-950/15 p-4 space-y-3 shadow-sm">
            {hasPix ? (
              <>
                <div className="space-y-2">
                  <div className="text-[10px] font-black uppercase tracking-widest text-rose-500 dark:text-rose-300">
                    Valor do QR Code estático
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                    {config.amounts.map((item) => (
                      <button
                        key={`pix-${item.value}`}
                        type="button"
                        onClick={() => setSelectedPixAmount(item.value)}
                        className={`h-10 rounded-2xl border text-xs font-black transition-colors ${selectedPixAmount === item.value
                          ? 'border-rose-500 bg-gradient-to-r from-rose-500 via-pink-500 to-orange-400 text-white shadow-md shadow-rose-500/20'
                          : 'border-rose-200 dark:border-rose-900/40 bg-rose-50 dark:bg-rose-950/20 text-rose-700 dark:text-rose-200'
                        }`}
                      >
                        {item.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="rounded-3xl border border-rose-200 dark:border-rose-900/40 bg-gradient-to-br from-white to-rose-50 dark:from-rose-950/30 dark:to-rose-950/10 p-4 flex flex-col items-center gap-3">
                  <div className="flex items-center gap-2 text-rose-600 dark:text-rose-300">
                    <QrCode size={16} />
                    <span className="text-[10px] font-black uppercase tracking-widest">
                      QR Code Pix estático {selectedPixAmount ? `• R$ ${selectedPixAmount}` : ''}
                    </span>
                  </div>
                  {pixQrCodeDataUrl ? (
                    <img
                      src={pixQrCodeDataUrl}
                      alt={`QR Code Pix de R$ ${selectedPixAmount}`}
                      className="w-full max-w-[13rem] aspect-square rounded-2xl bg-white p-3 border border-rose-100 shadow-sm"
                    />
                  ) : (
                    <div className="w-full max-w-[13rem] aspect-square rounded-2xl bg-white/80 dark:bg-rose-950/30 border border-rose-100 dark:border-rose-900/30 flex items-center justify-center text-xs text-rose-400">
                      Gerando QR Code...
                    </div>
                  )}
                </div>

                <div className="text-[10px] font-black uppercase tracking-widest text-rose-500 dark:text-rose-300">{config.pixLabel}</div>
                <code className="block break-all rounded-2xl bg-rose-50 dark:bg-rose-950/40 border border-rose-200 dark:border-rose-900/40 px-4 py-3 text-[12px] text-rose-900 dark:text-rose-100">
                  {config.pixKey}
                </code>
                <div className="text-[10px] font-black uppercase tracking-widest text-rose-500 dark:text-rose-300">Pix copia e cola</div>
                <code className="block break-all rounded-2xl bg-rose-50 dark:bg-rose-950/40 border border-rose-200 dark:border-rose-900/40 px-4 py-3 text-[12px] text-rose-900 dark:text-rose-100 max-h-40 overflow-y-auto">
                  {pixCopyPasteCode}
                </code>
                <div className="flex flex-col sm:flex-row gap-2">
                  <button
                    type="button"
                    onClick={copyPixKey}
                    className="h-11 px-4 rounded-2xl bg-gradient-to-r from-rose-500 via-pink-500 to-orange-400 text-white text-xs font-black uppercase tracking-widest flex items-center justify-center gap-2 shadow-lg shadow-rose-500/20"
                  >
                    <Copy size={15} />
                    Copiar chave Pix
                  </button>
                  <button
                    type="button"
                    onClick={copyPixCode}
                    className="h-11 px-4 rounded-2xl border border-rose-200 dark:border-rose-900/40 bg-white/80 dark:bg-rose-950/20 text-rose-700 dark:text-rose-100 text-xs font-black uppercase tracking-widest flex items-center justify-center gap-2"
                  >
                    <Copy size={15} />
                    Copiar Pix copia e cola
                  </button>
                </div>
              </>
            ) : (
              <div className="rounded-2xl border border-dashed border-rose-200 dark:border-rose-900/40 bg-rose-50/80 dark:bg-rose-900/10 p-4 text-xs text-rose-800 dark:text-rose-200">
                Configure sua chave em `REACT_APP_SUPPORT_PIX_KEY` para habilitar o Pix no modal.
              </div>
            )}
          </div>
        </section>

        <section className="rounded-3xl border border-rose-200/80 dark:border-rose-900/30 bg-white/80 dark:bg-rose-950/15 p-4 sm:p-5 shadow-sm">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="min-w-0">
              <div className="flex items-center gap-2 text-rose-600 dark:text-rose-300">
                <Mail size={16} />
                <h4 className="text-sm font-black uppercase tracking-wider">Contato</h4>
              </div>
              <p className="mt-2 text-xs leading-5 text-gray-600 dark:text-gray-300">
                Para parcerias ou dúvidas sobre o projeto, entre em contato pelo e-mail abaixo.
              </p>
              <p className="mt-1 text-sm font-black text-gray-800 dark:text-gray-100 break-all">{CONTACT_EMAIL}</p>
            </div>
            <a
              href={CONTACT_MAILTO}
              className="inline-flex h-11 shrink-0 items-center justify-center gap-2 rounded-2xl bg-rose-600 px-4 text-xs font-black uppercase tracking-widest text-white transition-colors hover:bg-rose-700"
            >
              Enviar e-mail
              <ExternalLink size={14} />
            </a>
          </div>
        </section>
      </div>
    </ModalShell>
  );
}
